/* 발췌 카드 그리기 — 새 의존성 없이 canvas 2D 로 직접 그린다. 미리보기와 저장 PNG 가 같은 함수를 지나므로 보이는 그대로 저장된다.
   글자는 낱말(띄어쓰기) 단위로 줄을 바꾸고, 한 낱말이 한 줄보다 길면 글자 단위로 자른다. 글이 칸을 넘치면 글자를 줄여 맞추고,
   가장 작은 크기로도 넘치면 마지막 줄을 … 로 끝낸다. 이용자 이름 블러는 canvas filter, 지원하지 않는 브라우저(사파리)는 겹쳐 그려 흐린다. */
import { speechInner } from '@/features/veluvit/components/reader/reader-blocks'
import { SERIF_STACK } from '@/features/veluvit/stores/reader.store'

import type {
  ExcerptParagraph,
  ExcerptRatio,
  ExcerptStyle,
} from '@/features/veluvit/services/excerpt.service'

export type ExcerptCard = {
  paragraphs: ExcerptParagraph[]
  style: ExcerptStyle
  charName: string
  author: string
  userName: string
}

/** 저장 이미지 폭 — 인스타그램 · 스토리 기준 */
const EXPORT_WIDTH = 1080
const RATIOS: Record<ExcerptRatio, number> = {
  '1:1': 1,
  '3:4': 4 / 3,
  '9:16': 16 / 9,
}

export function cardSize(ratio: ExcerptRatio, width: number) {
  return { w: width, h: Math.round(width * RATIOS[ratio]) }
}

/** 글자 크기 단계(1~7) → 1080 폭 기준 px */
const FONT_PX = [34, 38, 43, 48, 54, 61, 68]
const MIN_PX = 24

const INK = { black: '#1b1a18', white: '#f7f5f0' }
const PLAIN_BG = { white: '#fbfaf7', black: '#0a0a0b' }

/* ---------- 글꼴 · 그림 준비 ---------- */

/** next/font 가 body 에 단 글꼴 변수(--font-pretendard 등)를 실제 글꼴 이름으로 읽는다 */
function cssFont(variable: string, fallback: string) {
  const value =
    typeof document === 'undefined'
      ? ''
      : getComputedStyle(document.body).getPropertyValue(variable).trim()
  return value ? `${value}, ${fallback}` : fallback
}

function family(font: ExcerptStyle['font']) {
  if (font === 'serif') return { family: SERIF_STACK, weight: 500, lead: 1.78 }
  // 저장된 display 값은 호환하되, 퀘스트용 폰트 대신 굵은 고딕으로 그린다.
  if (font === 'display')
    return {
      family: cssFont('--font-pretendard', '-apple-system, sans-serif'),
      weight: 700,
      lead: 1.65,
    }
  return {
    family: cssFont('--font-pretendard', '-apple-system, sans-serif'),
    weight: 500,
    lead: 1.72,
  }
}

const images = new Map<string, Promise<HTMLImageElement | null>>()

function tryImage(
  src: string,
  cors: boolean
): Promise<HTMLImageElement | null> {
  return new Promise((resolve) => {
    const img = new Image()
    if (cors) img.crossOrigin = 'anonymous'
    img.decoding = 'async'
    img.onload = () => resolve(img)
    img.onerror = () => resolve(null)
    img.src = src
  })
}

/**
 * 배경 그림 — 저장(toBlob)하려면 캔버스가 오염되면 안 된다. 다른 도메인 그림은 CORS 로 받아 보고,
 * 안 되면 우리 도메인의 이미지 최적화 경로(/_next/image)로 다시 받는다. 둘 다 안 되면 null(단색 바탕으로 그린다).
 */
function loadImage(src: string): Promise<HTMLImageElement | null> {
  const cached = images.get(src)
  if (cached) return cached
  const job = (async () => {
    if (src.startsWith('data:') || src.startsWith('blob:'))
      return tryImage(src, false)
    const direct = await tryImage(src, !src.startsWith('/'))
    if (direct) return direct
    return tryImage(
      `/_next/image?url=${encodeURIComponent(src)}&w=${EXPORT_WIDTH}&q=75`,
      false
    )
  })()
  images.set(src, job)
  return job
}

type ExcerptAssets = { image: HTMLImageElement | null }

/** 그리기 전에 기다릴 것 — 배경 그림과 글꼴(늦게 받는 웹폰트면 첫 그림이 대체 글꼴로 나온다) */
export async function prepareExcerpt(
  card: ExcerptCard
): Promise<ExcerptAssets> {
  const { style } = card
  const src =
    style.bg === 'scene'
      ? style.scene
      : style.bg === 'photo'
        ? style.photo
        : undefined
  const face = family(style.font)
  const sample =
    card.paragraphs
      .map((p) => p.text)
      .join(' ')
      .slice(0, 200) + card.charName
  const sans = cssFont('--font-pretendard', '-apple-system, sans-serif')
  const fonts = [
    `${face.weight} 40px ${face.family}`,
    `500 40px ${sans}`,
    `700 40px ${sans}`,
  ].map((font) =>
    document.fonts
      ? document.fonts.load(font, sample).catch(() => [])
      : Promise.resolve([])
  )
  const [image] = await Promise.all([
    src ? loadImage(src) : Promise.resolve(null),
    ...fonts,
  ])
  return { image }
}

/* ---------- 글 다듬기 · 줄 나누기 ---------- */

type Run = { t: string; mask: boolean }
type Word = { runs: Run[]; glue: boolean }
type Line = Word[]

const escape = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

/** 한 문단 → 글 조각. 대사는 “ ” 로 감싸고, 키워드를 바꾸고, 이용자 이름을 가린다 */
function excerptRuns(
  p: ExcerptParagraph,
  style: ExcerptStyle,
  userName: string
): Run[] {
  let text = p.kind === 'speech' ? `“${speechInner(p.text)}”` : p.text.trim()
  for (const swap of style.swaps) {
    if (swap.from.trim()) text = text.split(swap.from.trim()).join(swap.to)
  }
  const name = userName.trim()
  if (!style.maskOn || !name) return [{ t: text, mask: false }]
  if (style.mask === 'initial') {
    const chars = Array.from(name)
    return [
      {
        t: text
          .split(name)
          .join(chars[0] + '○'.repeat(Math.max(1, chars.length - 1))),
        mask: false,
      },
    ]
  }
  if (style.mask === 'replace')
    return [
      {
        t: text.split(name).join(style.maskText.trim() || '그대'),
        mask: false,
      },
    ]
  return text
    .split(new RegExp(`(${escape(name)})`))
    .filter(Boolean)
    .map((t) => ({ t, mask: t === name }))
}

/** 조각 → 낱말(띄어쓰기로 나눈다. 가린 이름은 쪼개지 않는다) */
function toWords(runs: Run[]): Word[] {
  const out: Word[] = []
  let current: Run[] = []
  for (const run of runs) {
    if (run.mask) {
      current.push(run)
      continue
    }
    run.t.split(/[ \t]+/).forEach((part, index) => {
      if (index > 0 && current.length) {
        out.push({ runs: current, glue: false })
        current = []
      }
      if (part) current.push({ t: part, mask: false })
    })
  }
  if (current.length) out.push({ runs: current, glue: false })
  return out
}

function wrapParagraph(
  ctx: CanvasRenderingContext2D,
  runs: Run[],
  max: number,
  pad: number
): Line[] {
  const runWidth = (r: Run) =>
    ctx.measureText(r.t).width + (r.mask ? pad * 2 : 0)
  const wordWidth = (w: Word) => w.runs.reduce((sum, r) => sum + runWidth(r), 0)
  const space = ctx.measureText(' ').width
  // 한 줄보다 긴 낱말은 글자 단위로 자른다(이어 붙는 조각은 glue — 앞에 띄어쓰기 없음)
  const words: Word[] = []
  for (const word of toWords(runs)) {
    if (wordWidth(word) <= max) {
      words.push(word)
      continue
    }
    let chunk: Run[] = []
    let width = 0
    let glue = false
    for (const run of word.runs) {
      const pieces = run.mask
        ? [run]
        : Array.from(run.t).map((t) => ({ t, mask: false }))
      for (const piece of pieces) {
        const w = runWidth(piece)
        if (width + w > max && chunk.length) {
          words.push({ runs: chunk, glue })
          chunk = []
          width = 0
          glue = true
        }
        chunk.push(piece)
        width += w
      }
    }
    if (chunk.length) words.push({ runs: chunk, glue })
  }
  const lines: Line[] = []
  let line: Line = []
  let width = 0
  for (const word of words) {
    const w = wordWidth(word)
    const gap = line.length && !word.glue ? space : 0
    if (line.length && width + gap + w > max) {
      lines.push(line)
      line = [{ ...word, glue: true }]
      width = w
    } else {
      line.push(word)
      width += gap + w
    }
  }
  if (line.length) lines.push(line)
  return lines
}

function lineWidth(ctx: CanvasRenderingContext2D, line: Line, pad: number) {
  const space = ctx.measureText(' ').width
  return line.reduce(
    (sum, word, i) =>
      sum +
      (i > 0 && !word.glue ? space : 0) +
      word.runs.reduce(
        (s, r) => s + ctx.measureText(r.t).width + (r.mask ? pad * 2 : 0),
        0
      ),
    0
  )
}

/* ---------- 그리기 ---------- */

function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number
) {
  ctx.beginPath()
  if (typeof ctx.roundRect === 'function') ctx.roundRect(x, y, w, h, r)
  else ctx.rect(x, y, w, h)
}

/** 가린 이름 — 글자 수만큼의 흐린 알약 안에 살짝 비치는 글자 모양 */
function drawMask(
  ctx: CanvasRenderingContext2D,
  text: string,
  x: number,
  y: number,
  px: number,
  pad: number,
  ink: string
) {
  const w = ctx.measureText(text).width + pad * 2
  const h = px * 1.12
  ctx.save()
  roundRect(ctx, x, y - h / 2, w, h, h / 2)
  ctx.globalAlpha = 0.16
  ctx.fillStyle = ink
  ctx.fill()
  ctx.clip()
  ctx.fillStyle = ink
  if (typeof ctx.filter === 'string') {
    ctx.filter = `blur(${Math.max(2, px * 0.17)}px)`
    ctx.globalAlpha = 0.7
    ctx.fillText(text, x + pad, y)
    ctx.filter = 'none'
  } else {
    // canvas filter 가 없는 브라우저 — 조금씩 어긋나게 여러 번 옅게 찍어 흐림을 흉내 낸다
    const r = px * 0.12
    ctx.globalAlpha = 0.1
    for (const [dx, dy] of [
      [-r, 0],
      [r, 0],
      [0, -r],
      [0, r],
      [-r * 0.7, -r * 0.7],
      [r * 0.7, r * 0.7],
      [r * 0.7, -r * 0.7],
      [-r * 0.7, r * 0.7],
    ]) {
      ctx.fillText(text, x + pad + dx, y + dy)
    }
  }
  ctx.restore()
  return w
}

function drawCover(
  ctx: CanvasRenderingContext2D,
  img: HTMLImageElement,
  w: number,
  h: number
) {
  const scale = Math.max(w / img.naturalWidth, h / img.naturalHeight)
  const dw = img.naturalWidth * scale
  const dh = img.naturalHeight * scale
  // 얼굴이 보통 위쪽에 있어 세로로 남는 부분은 위 1/5 지점을 기준으로 자른다
  ctx.drawImage(img, (w - dw) / 2, (h - dh) * 0.2, dw, dh)
}

function withSpacing(
  ctx: CanvasRenderingContext2D,
  spacing: string,
  draw: () => void
) {
  const styled = ctx as CanvasRenderingContext2D & { letterSpacing?: string }
  const before = styled.letterSpacing
  if (before !== undefined) styled.letterSpacing = spacing
  draw()
  if (before !== undefined) styled.letterSpacing = before
}

/**
 * 카드를 canvas 에 그린다. width 는 캔버스 픽셀 폭(미리보기는 작게, 저장은 1080) — 모든 치수가 이 폭에 비례한다.
 * assets 는 prepareExcerpt 가 기다려 둔 것.
 */
export function paintExcerpt(
  canvas: HTMLCanvasElement,
  card: ExcerptCard,
  assets: ExcerptAssets,
  width: number
) {
  const { style } = card
  const { w, h } = cardSize(style.ratio, width)
  canvas.width = w
  canvas.height = h
  const ctx = canvas.getContext('2d')
  if (!ctx) return
  const unit = w / EXPORT_WIDTH
  const ink = INK[style.color]
  const lightInk = style.color === 'white'

  /* 바탕 */
  const picture =
    style.bg === 'scene' || style.bg === 'photo' ? assets.image : null
  if (picture) {
    drawCover(ctx, picture, w, h)
    // 글이 읽히도록 덮는 막 — 흰 글자면 어둡게, 검은 글자면 밝게. 아래쪽(이름 · 워터마크)은 조금 더
    ctx.fillStyle = lightInk ? 'rgba(0,0,0,0.46)' : 'rgba(255,255,255,0.6)'
    ctx.fillRect(0, 0, w, h)
    const fade = ctx.createLinearGradient(0, h * 0.62, 0, h)
    fade.addColorStop(0, lightInk ? 'rgba(0,0,0,0)' : 'rgba(255,255,255,0)')
    fade.addColorStop(
      1,
      lightInk ? 'rgba(0,0,0,0.5)' : 'rgba(255,255,255,0.45)'
    )
    ctx.fillStyle = fade
    ctx.fillRect(0, 0, w, h)
  } else {
    ctx.fillStyle =
      style.bg === 'white'
        ? PLAIN_BG.white
        : style.bg === 'black'
          ? PLAIN_BG.black
          : '#16161a'
    ctx.fillRect(0, 0, w, h)
  }

  /* 칸 나누기 */
  const padX = Math.round(w * 0.1)
  const footerH = Math.round(w * 0.17)
  const top = Math.round(Math.max(w * 0.12, h * 0.1))
  const bottom = h - footerH - Math.round(w * 0.05)
  const maxW = w - padX * 2
  const face = family(style.font)

  /* 글 — 넘치면 줄여서 맞춘다 */
  const runs = card.paragraphs.map((p) => excerptRuns(p, style, card.userName))
  let px = (FONT_PX[style.size - 1] ?? FONT_PX[3]) * unit
  let layout: Line[][] = []
  let lead = px * face.lead
  let gap = px * 0.8
  let total = 0
  for (;;) {
    ctx.font = `${face.weight} ${px}px ${face.family}`
    const pad = px * 0.18
    layout = runs.map((r) => wrapParagraph(ctx, r, maxW, pad))
    lead = px * face.lead
    gap = px * 0.8
    total =
      layout.reduce((sum, lines) => sum + lines.length * lead, 0) +
      gap * Math.max(0, layout.length - 1)
    if (total <= bottom - top || px <= MIN_PX * unit) break
    px = Math.max(MIN_PX * unit, px * 0.92)
  }
  const pad = px * 0.18

  // 가장 작게도 넘치면 들어가는 줄까지만 그리고 마지막 줄을 … 로 끝낸다
  const room = bottom - top
  let flat: { line: Line; paraStart: boolean }[] = layout.flatMap((lines) =>
    lines.map((line, i) => ({ line, paraStart: i === 0 }))
  )
  if (total > room) {
    const kept: typeof flat = []
    let used = 0
    for (const item of flat) {
      const need = lead + (item.paraStart && kept.length ? gap : 0)
      if (used + need > room) break
      kept.push(item)
      used += need
    }
    const last = kept.at(-1)
    if (last)
      last.line = [
        ...last.line,
        { runs: [{ t: '…', mask: false }], glue: true },
      ]
    flat = kept
    total = used
  }

  const startY = top + Math.max(0, (room - total) / 2)
  const anchorX =
    style.align === 'center' ? w / 2 : style.align === 'right' ? w - padX : padX

  // 글 위 짧은 가는 선 — 인용 카드의 머리
  ctx.fillStyle = ink
  ctx.globalAlpha = 0.45
  const rule = 56 * unit
  const ruleX =
    style.align === 'center'
      ? w / 2 - rule / 2
      : style.align === 'right'
        ? w - padX - rule
        : padX
  ctx.fillRect(ruleX, startY - 44 * unit, rule, Math.max(1, 3 * unit))
  ctx.globalAlpha = 1

  ctx.font = `${face.weight} ${px}px ${face.family}`
  ctx.textBaseline = 'middle'
  ctx.textAlign = 'left'
  ctx.fillStyle = ink
  const space = ctx.measureText(' ').width
  let y = startY
  flat.forEach((item, index) => {
    if (item.paraStart && index > 0) y += gap
    const lw = lineWidth(ctx, item.line, pad)
    let x =
      style.align === 'center'
        ? anchorX - lw / 2
        : style.align === 'right'
          ? anchorX - lw
          : anchorX
    const mid = y + lead / 2
    item.line.forEach((word, i) => {
      if (i > 0 && !word.glue) x += space
      for (const run of word.runs) {
        if (run.mask) x += drawMask(ctx, run.t, x, mid, px, pad, ink)
        else {
          ctx.fillText(run.t, x, mid)
          x += ctx.measureText(run.t).width
        }
      }
    })
    y += lead
  })

  /* 아래 — 캐릭터 이름 · 작가 · veluvit 워터마크 */
  const sans = cssFont('--font-pretendard', '-apple-system, sans-serif')
  const baseY = h - Math.round(w * 0.085)
  ctx.textBaseline = 'alphabetic'
  ctx.globalAlpha = 0.28
  ctx.fillRect(padX, baseY - 74 * unit, maxW, Math.max(1, 1.5 * unit))
  ctx.globalAlpha = 0.95
  ctx.font = `700 ${36 * unit}px ${sans}`
  ctx.fillText(card.charName, padX, baseY - 16 * unit, maxW * 0.62)
  ctx.globalAlpha = 0.62
  ctx.font = `500 ${24 * unit}px ${sans}`
  if (card.author)
    ctx.fillText(`작가 ${card.author}`, padX, baseY + 22 * unit, maxW * 0.62)
  ctx.globalAlpha = 0.9
  ctx.textAlign = 'right'
  ctx.font = `700 ${40 * unit}px ${sans}`
  withSpacing(ctx, `${4 * unit}px`, () =>
    ctx.fillText('veluvit', w - padX, baseY + 2 * unit)
  )
  ctx.globalAlpha = 1
  ctx.textAlign = 'left'
}

/** 저장용 PNG — 1080 폭으로 다시 그린다 */
export async function renderExcerptBlob(card: ExcerptCard): Promise<Blob> {
  const assets = await prepareExcerpt(card)
  const canvas = document.createElement('canvas')
  paintExcerpt(canvas, card, assets, EXPORT_WIDTH)
  return new Promise((resolve, reject) => {
    try {
      canvas.toBlob(
        (blob) =>
          blob ? resolve(blob) : reject(new Error('이미지를 만들지 못했어요')),
        'image/png'
      )
    } catch {
      // 배경 그림이 다른 도메인이라 캔버스가 막혔다
      reject(
        new Error(
          '이 배경 그림으로는 저장할 수 없어요. 흰 · 검정 배경으로 바꿔 주세요.'
        )
      )
    }
  })
}

/** 내 사진 — 긴 변 1440px JPEG 로 줄여 data URL 로(기기 저장 · 그리기 둘 다 가볍게) */
export async function shrinkPhoto(file: File): Promise<string> {
  const url = URL.createObjectURL(file)
  try {
    const img = await tryImage(url, false)
    if (!img) throw new Error('사진을 열지 못했어요')
    const scale = Math.min(
      1,
      1440 / Math.max(img.naturalWidth, img.naturalHeight)
    )
    const canvas = document.createElement('canvas')
    canvas.width = Math.round(img.naturalWidth * scale)
    canvas.height = Math.round(img.naturalHeight * scale)
    canvas.getContext('2d')?.drawImage(img, 0, 0, canvas.width, canvas.height)
    return canvas.toDataURL('image/jpeg', 0.84)
  } finally {
    URL.revokeObjectURL(url)
  }
}

/* ---------- 저장 · 공유 ---------- */

/** 기기에 PNG 저장 — 다운로드 링크를 잠깐 만들어 누른다 */
export function downloadBlob(blob: Blob, name: string) {
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = name
  document.body.appendChild(link)
  link.click()
  link.remove()
  setTimeout(() => URL.revokeObjectURL(url), 4000)
}

/** 파일 공유(Web Share)를 쓸 수 있는 브라우저인가 — 모바일 대부분, PC 는 일부 */
export function canShareFiles() {
  if (
    typeof navigator === 'undefined' ||
    typeof navigator.canShare !== 'function'
  )
    return false
  try {
    return navigator.canShare({
      files: [new File([''], 'probe.png', { type: 'image/png' })],
    })
  } catch {
    return false
  }
}

/** 공유 시트 열기. 이용자가 닫으면(AbortError) 조용히 끝낸다 — 그 밖의 실패만 던진다 */
export async function shareBlob(blob: Blob, name: string, title: string) {
  const file = new File([blob], name, { type: 'image/png' })
  try {
    await navigator.share({ files: [file], title })
  } catch (e) {
    if (e instanceof DOMException && e.name === 'AbortError') return
    throw new Error('공유하지 못했어요. 저장한 뒤 직접 올려 주세요.')
  }
}

/** 저장 파일 이름 — veluvit-캐릭터-날짜.png */
export function excerptFileName(charName: string) {
  const day = new Date().toISOString().slice(0, 10).replace(/-/g, '')
  return `veluvit-${charName.replace(/[\/:*?"<>|\s]+/g, '')}-${day}.png`
}
